import { notFoundPage } from "@/utils/notFound";
import { GetServerSideProps } from "next";
import CategoryHub from "@/components/hub/CategoryHub";
import type { HubCategory } from "@/utils/categoryHubs";
import { BADGE_ALIASES } from "@/utils/badgeAliases";
import { localePath } from "@/utils/localeSeo";

interface Props {
  category: HubCategory;
  // Fewer than HUB_MIN_VOICES voices: served, but noindex.
  thin: boolean;
  related: HubCategory[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  voices: any[];
  page: number;
  lastPage: number;
}

export default function CategoryVoicesPage(props: Props) {
  return (
    <CategoryHub
      // Fresh sort/search state per page: a client-side hop between pages
      // reuses this component otherwise.
      key={`${props.category.slug}:${props.page}`}
      category={props.category}
      thin={props.thin}
      related={props.related}
      voices={props.voices}
      page={props.page}
      lastPage={props.lastPage}
    />
  );
}

// Page 1 is the trending grid. ?page=N (N >= 2) lists the whole category in a
// stable order (see fetchCategoryPage) so every voice has a crawlable link.
export const getServerSideProps: GetServerSideProps = async ({
  params,
  query,
  res,
  locale,
}) => {
  const slug = String(params?.category || "");
  let page = 1;
  if (query.page !== undefined) {
    if (typeof query.page !== "string" || !/^[1-9][0-9]{0,5}$/.test(query.page))
      return notFoundPage({ res });
    page = Number(query.page);
    if (page === 1) {
      return {
        redirect: {
          destination: localePath(locale, `/ai-voices/${slug}`),
          permanent: true,
        },
      };
    }
  }
  try {
    const {
      slugifyBadge,
      resolveCategorySlug,
      getHubCategories,
      fetchCategoryVoices,
      fetchCategoryPage,
      getCategoryVoiceIds,
      hubLastPage,
      HUB_PAGE_SIZE,
      HUB_MIN_VOICES,
      MAX_HUB_PAGE,
    } = await import("@/utils/categoryHubs");

    // A typo of another badge (/ai-voices/deutsc) goes to that badge's hub.
    const alias = Object.entries(BADGE_ALIASES).find(
      ([from]) => slugifyBadge(from) === slug,
    );
    if (alias) {
      return {
        redirect: {
          destination: localePath(
            locale,
            `/ai-voices/${slugifyBadge(alias[1])}`,
          ),
          permanent: true,
        },
      };
    }

    const category = await resolveCategorySlug(slug);
    if (!category) return notFoundPage({ res });
    if (page > MAX_HUB_PAGE) return notFoundPage({ res });

    const [listing, all] = await Promise.all([
      page === 1
        ? Promise.all([
            fetchCategoryVoices(category.badge, HUB_PAGE_SIZE),
            // Only sizes the page links; page 1 renders without them.
            getCategoryVoiceIds(category.badge).catch((error) => {
              console.error("hub voice ids failed:", slug, error);
              return [] as string[];
            }),
          ]).then(([voices, ids]) => ({
            voices,
            lastPage: hubLastPage(ids.length),
          }))
        : fetchCategoryPage(category.badge, page),
      getHubCategories(HUB_MIN_VOICES),
    ]);
    if (page > listing.lastPage || (page > 1 && listing.voices.length === 0))
      return notFoundPage({ res });
    const related = all.filter((c) => c.slug !== category.slug).slice(0, 12);

    res.setHeader(
      "Cache-Control",
      "public, s-maxage=600, stale-while-revalidate=1200",
    );

    return {
      props: {
        category,
        thin: category.count < HUB_MIN_VOICES,
        related,
        voices: listing.voices,
        page,
        lastPage: listing.lastPage,
      },
    };
  } catch (error) {
    console.error("Category hub SSR error:", slug, error);
    // A failed lookup is a 500, not "this hub does not exist".
    res.setHeader("Cache-Control", "private, no-store");
    throw error;
  }
};
